import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { FileInput, Overlay, ColorTypes, Button, getThemeManager, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Grid, GridEditEvent, GridEvent, GridSource, TableTheme } from "@omegagrid/grid";
import { AppComponent } from '../component';
import rp from "../global"
import { Service } from "../client";
import { style } from "./fileManager.style";
import * as helpers from "../helpers";
import { ChangeEvent } from "@omegagrid/core/dist/common/events";
import { Accounting, AccountingGroup, File as FileEntity } from "@reporting/rp-types/core";

export class FileManagerEvent extends Event {
	manager: FileManager;
	file: FileEntity;

	constructor(type: string, args?: Partial<FileManagerEvent>) {
		super(`filemanager.${type}`, {bubbles: true, composed: true});
		Object.assign(this, args);
	}
}

export const DEFAULT_FILE_MANAGER_OPTIONS: FileManagerOptions = {
	showFilesGrid: true,
	dispatchCloseEvent: true,
	maxFileSize: 200000, // 200 KB
	lightTheme: {
		name: 'light',
		cells: [{
			conditions: [{property: 'editable', value: false}],
			style: {fgColor: 'dedede'}
		}]
	},
	darkTheme: {
		name: 'dark',
		cells: [{
			conditions: [{property: 'editable', value: false}],
			style: {fgColor: '171717', color: '9b9b9b'}
		}]
	},
	endpoints: {
		getFile: 'files/{id}',
		getFiles: 'files',
		postFile: 'files',
		deleteFile: 'files/{id}',
		downloadFile: 'files/{id}/download',
	}
};

export type FileManagerEndpoints = {
	getFile?: string;
	getFiles?: string;
	postFile?: string;
	deleteFile?: string;
	downloadFile?: string;
};

export type FileManagerOptions = {
	maxFileSize: number;
	showFilesGrid?: boolean;
	extensions?: string[];
	dispatchCloseEvent?: boolean;
	lightTheme?: TableTheme,
	darkTheme?: TableTheme,
	endpoints?: FileManagerEndpoints;
}

@customElement('rp-definition-filemanager')
export class FileManager extends AppComponent {

	static styles = [style];

	@property({ type: Object })
	options: FileManagerOptions = DEFAULT_FILE_MANAGER_OPTIONS;

	@property({ type: Object })
	accounting: Accounting;

	@property({ type: Object })
	group: AccountingGroup;

	@property({ type: Object })
	client: Service;

	@query('og-fileinput')
	fileInput: FileInput;

	@query('#files')
	filesDiv: HTMLElement;

	@query('og-overlay')
	loader: Overlay;

	@query('og-grid')
	filesGrid: Grid;

	@query('#uploadBtn')
	uploadBtn: Button;

	private file: FileEntity;
	public files: FileEntity[];

	get activeThemeType() { return getThemeManager().activeTheme.type }
	get activeTheme() {
		return this.activeThemeType === 'dark' ? this.options?.darkTheme : this.options?.lightTheme;
	}

	_lastThemeType: string;
	updateTheme = () => {
		if (this._lastThemeType === this.activeThemeType || !this.filesGrid) return;
		this._lastThemeType = this.activeThemeType;
		this.filesGrid.model.applyTheme(this.activeTheme, true);
		this.filesGrid.rendering.render(true);
	};

	connectedCallback(): void {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this.updateTheme);
		if (this._lastThemeType != null) this.updateTheme();
	}

	disconnectedCallback(): void {
		this._lastThemeType = this.activeThemeType;
		getThemeManager().removeEventListener('change', this.updateTheme);
	}

	async updated(changedProperties: Map<PropertyKey, unknown>) {
		super.updated(changedProperties);
		await this.loadFilesGrid();
	}

	async loadFilesGrid() {
		if (!this.filesGrid) return;
		this.loader.show(this.filesDiv);
		this.filesGrid.setSourceData(await this.createGridData());
		this.loader.hide();
	}

	async createGridData(): Promise<GridSource> {
		const gridData: GridSource = {};
		gridData.options = {
			colLetters: false,
			rowNumbers: false,
			filters: true,
			columnSorting: true,
			fontSize: 12,
			rowHeight: 27,
			dateFormat: dates.getDateFormat({withTime: true}),
		};
		gridData.columns = [{
				t: 'a',
				e: false,
				w: 5 + 20 * 3,
				content: [{
					action: 'deleteFile',
					title: msg('Delete'),
					icon: 'trash',
					color: ColorTypes.Red,
				}, {
					action: 'downloadFile',
					title: msg('Download'),
					icon: 'download',
					color: ColorTypes.Accent,
				}, {
					action: 'selectFile',
					title: msg('Select'),
					icon: 'check',
					color: ColorTypes.Blue,
				}],
				filter: false,
				sortable: false
			}, {
				data: 'filename',
				header: msg('Name'),
				t: 't',
				e: true,
				filter: true,
				w: 220
			}, {
				data: 'who_created',
				header: msg('Created by'),
				t: 't',
				e: false,
				w: 140
			}, {
				data: 'date_created',
				header: msg('Created'),
				t: 'dt',
				e: false,
				w: 145
			}, {
				data: 'id_file',
				t: 'n',
				e: false,
				hidden: true,
			}, {
				data: 'extension',
				t: 't',
				e: false,
				hidden: true,
			}
		];
		const theme = this.activeTheme;
		if (theme) gridData.theme = theme;
		this.files = await this.client.get<FileEntity[]>(this.options.endpoints.getFiles);
		gridData.rows = this.files.map(data => ({ data }));
		return gridData;
	}

	async renameFile(id_file: number, filename: string): Promise<void> {
		await this.client.post(this.options.endpoints.postFile, { id_file, filename } as FileEntity);
	}

	async deleteFile(id: number): Promise<void> {
		await this.client.delete(this.options.endpoints.deleteFile.replace('{id}', id.toString()));
	}

	async uploadFile(file: FileEntity): Promise<void> {
		await this.client.post(this.options.endpoints.postFile, file);
	}

	resetFileInput() {
		this.file = null;
		this.fileInput.value = null;
	}

	toggleControls(disable: boolean = false) {
		this.uploadBtn.icon = disable ? 'hourglass-half' : 'cloud-arrow-up';
		this.uploadBtn.disabled = disable;
		this.fileInput.disabled = disable;
	}

	refresh = () => {
		this.loadFilesGrid();
	}

	layout() {
		this.filesGrid?.layout();
	}

	_onGridAction = async (e: GridEvent) => {
		switch (e.action) {
			case 'deleteFile':
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: msg('Are you sure you want to delete this file?'),
				});
				if (command?.id == 'Y') {
					await this.deleteFile(e.row.data.id_file).then(() => {
						this.loadFilesGrid();
					}).catch((err) => {
						rp.host.bucket.showAlert(err[0]);
					});
				}
				break;
			case 'downloadFile':
				await this.client.download(this.options.endpoints.downloadFile.replace('{id}', e.row.data.id_file.toString()), {
					fileName: `${e.row.data.filename}.${e.row.data.extension || 'txt'}`
				});
				break;
			case 'selectFile':
				const file = this.files.find(f => f.id_file === e.row.data.id_file);
				this.dispatchEvent(new FileManagerEvent('select', { file }));
				if (this.options.dispatchCloseEvent) this.dispatchEvent(new Event('close'));
				break;
		}
	}

	_onGridEdit = async (e: GridEditEvent) => {
		if (e.editType !== 'value') return;
		e.cells.forEach(async (c, i) => {
			if (c.column.data === 'filename' && e.oldValues[i] !== c.value) {
				await this.renameFile(c.row.data.id_file, c.value as string).then(() => {
					rp.host.bucket.showAlert({ content: `${msg('File')} #${c.row.data.id_file} ${msg('renamed successfully')}.`, type: 'success' });
				}).catch(err => {
					rp.host.bucket.showAlert(err[0]);
				});
			}
		});
	}

	_onFileInputChange = async (e: ChangeEvent<File>) => {
		if (!e.value) {
			this.resetFileInput();
			return;
		}
		// Check file size
		if (e.value.size > this.options.maxFileSize) {
			this.resetFileInput();
			rp.host.bucket.showAlert({ content: msg('File is too large!'), type: 'warning' });
			return;
		}
		// Check file extension
		const extension = e.value.type.split('/').pop();
		if (this.options.extensions && !this.options.extensions.includes(extension)) {
			this.resetFileInput();
			rp.host.bucket.showAlert({ content: msg('Invalid file type!'), type: 'warning' });
			return;
		}
		const base64Content = await helpers.fileToDataUrl(e.value);
		this.file = {
			filename: e.value.name,
			content_base64: base64Content.split(',')[1],
			size: e.value.size,
			mime_type: e.value.type,
			extension: extension,
			orig_filename: e.value.name,
			scope: 'A',
		};
		//console.log('File to upload:', this.file);
	}

	_onUploadClick = async () => {
		if (!this.file) return;
		this.toggleControls(true);
		await this.uploadFile(this.file).then(() => {
			this.resetFileInput();
			this.loadFilesGrid();
		}).catch((e) => {
			rp.host.bucket.showAlert(e[0]);
		});
		this.toggleControls(false);
	}

	render = () => html`
		<div class="controls">
			${msg('File')}:
			<og-fileinput @change="${this._onFileInputChange}"></og-fileinput>
			<og-button
				id="uploadBtn"
				icon="cloud-arrow-up"
				color="accent"
				text="${msg('Upload')}"
				@click="${this._onUploadClick}">
			</og-button>
			<i>${msg('Max size')}: ${this.options?.maxFileSize / 1000} kB</i>
			${this.options?.showFilesGrid ? html`
			<og-button
				icon="arrows-rotate"
				color="gray"
				style="margin-left: 10px"
				@click="${this.refresh}">
			</og-button>
			` : ''}
		</div>

		<div class="body" id="files">
			${this.options?.showFilesGrid ? html`
			<og-overlay defaultLoader></og-overlay>
			<og-grid
				@grid.action="${this._onGridAction}"
				@grid.edit="${this._onGridEdit}">
			</og-grid>
			` : ``}
		</div>

	`;

}
